/**
 * Returns a user-friendly message for wallet errors.
 * Detects EIP-1193 rejection (code 4001) and ethers ACTION_REJECTED.
 */
export function friendlyError(error: unknown): string {
  if (!error) return "Unknown error";

  let raw = "";
  try {
    raw = JSON.stringify(error) || "";
  } catch {
    /* Wallet errors can contain BigInts or circular RPC data. */
  }
  const detail =
    `${raw} ${error instanceof Error ? error.message : String(error)}`.toLowerCase();
  if (detail.includes("0x807846a9") || detail.includes("invalidfeequote")) {
    return "The transaction authorization could not be validated. Refresh your balance and try again.";
  }
  if (
    /insufficient_funds|insufficient funds|insufficient balance|insufficient spendable private balance|exceeds balance|exceeds the balance|transfer amount exceeds/.test(
      detail,
    )
  ) {
    return detail.includes("private balance")
      ? "Insufficient private balance. Refresh your balance and enter a smaller amount."
      : "Insufficient wallet balance for this amount and the network fee. Enter a smaller amount or use MAX.";
  }

  // ethers v6 ACTION_REJECTED
  if (
    typeof error === "object" &&
    "code" in error &&
    (error as any).code === "ACTION_REJECTED"
  ) {
    return "Transaction rejected by user";
  }

  // EIP-1193 user rejection (code 4001) nested anywhere in the error
  if (raw.includes('"code":4001') || raw.includes('"code": 4001')) {
    return "Transaction rejected by user";
  }

  if (
    raw.includes("0x18a4b813") ||
    raw.toLowerCase().includes("invalidteesignature")
  ) {
    return "Base TEE signer is not currently valid onchain for this pool. The prover operator needs to refresh the enclave key registration in the attestation registry.";
  }

  if (
    raw.includes("0xd69c4112") ||
    raw.toLowerCase().includes("invalidsettlementreceiptexpiry")
  ) {
    return "Base settlement receipt expired before submission. Please retry.";
  }

  // Fallback to message string, but strip noisy ethers prefix
  const msg = error instanceof Error ? error.message : String(error);

  if (
    msg.toLowerCase().includes("user denied") ||
    msg.toLowerCase().includes("user rejected") ||
    msg.toLowerCase().includes("ethers-user-denied")
  ) {
    return "Transaction rejected by user";
  }

  return msg;
}
